/**
 * Deterministic citation metadata and Excel source-Note helpers.
 *
 * This mirrors data-feed's Explorer citation contract while keeping host
 * mechanics local to the add-in: research responses are indexed by their
 * exact citation IDs, cited writes are compiled into internal Note operations,
 * and no IDs or transient authentication URLs are written into the workbook.
 */

import {
  formatCellAddress,
  parseCellRangeAddress,
  parseSingleCellAddress,
} from "./excelCellAddress";
import type {
  CellAnnotationOperation,
  CellModification,
  RangeModification,
  WorkbookModification,
  WorksheetModification,
} from "./types";

export interface CitationNoteRecord {
  citation_id: string;
  source?: string;
  document?: string;
  category?: string;
  company?: string;
  published_date?: string;
  ticker?: string;
  fql_formula?: string;
  field?: string;
}

export type CitationNoteIndex = Record<string, CitationNoteRecord>;
export type CitationIdsMatrix = Array<Array<string[] | null>>;

export interface CitationNoteTarget {
  sheetName: string;
  address: string;
  citationIds: string[];
  noteText: string;
}

export interface PreparedCitationWrites {
  modifications: WorkbookModification;
  targets: CitationNoteTarget[];
  /** Citation-only issues retained for engineering diagnostics. */
  issues: string[];
}

export interface SourceNoteAudit {
  expectedCells: string[];
  presentCells: string[];
  missingCells: string[];
  unresolvedCells: string[];
  clean: boolean;
}

export const MAX_EXCEL_NOTE_LENGTH = 30_000;

const NOTE_FIELD_LIMITS: Record<
  Exclude<keyof CitationNoteRecord, "citation_id">,
  number
> = {
  source: 200,
  document: 500,
  category: 200,
  company: 300,
  published_date: 100,
  ticker: 200,
  field: 500,
  fql_formula: 4_000,
};

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? value as Record<string, unknown>
    : undefined;
}

function nonEmptyString(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed || undefined;
}

function firstField(
  record: Record<string, unknown> | undefined,
  ...names: string[]
): unknown {
  if (!record) return undefined;
  for (const name of names) {
    if (Object.prototype.hasOwnProperty.call(record, name)) {
      return record[name];
    }
  }
  return undefined;
}

function titleCaseSource(value: string): string {
  const words = value
    .split("_").join(" ")
    .split(" ")
    .map((word) => word.trim())
    .filter(Boolean);
  return words.map((word) => {
    const lower = word.toLowerCase();
    if (lower === "factset") return "FactSet";
    if (lower === "sec") return "SEC";
    if (lower === "ir") return "IR";
    return `${lower.charAt(0).toUpperCase()}${lower.slice(1)}`;
  }).join(" ");
}

function sourceLabel(
  metadata: Record<string, unknown> | undefined,
  client: unknown,
): string {
  const source = nonEmptyString(firstField(metadata, "dataSource", "data_source"));
  if (source) {
    const normalized = source.toLowerCase().replace(/[-\s]+/g, "_");
    if (normalized === "sec" || normalized === "sec_filings") return "SEC";
    return titleCaseSource(source);
  }
  const clientName = nonEmptyString(client);
  return clientName ? titleCaseSource(clientName) : "External Source";
}


interface MarkupTag {
  name: string;
  attributes: Record<string, string>;
  start: number;
  end: number;
  closing: boolean;
}

function isTagNameCharacter(character: string): boolean {
  const code = character.charCodeAt(0);
  return (
    (code >= 65 && code <= 90) ||
    (code >= 97 && code <= 122) ||
    (code >= 48 && code <= 57) ||
    character === "-" ||
    character === ":"
  );
}

/**
 * Read opening-tag attributes with a small stateful scanner. Citation payloads
 * are HTML fragments embedded in Markdown, so a full document parser would be
 * both environment-dependent and more permissive than the source contract.
 */
function scanMarkupTags(value: string): MarkupTag[] {
  const tags: MarkupTag[] = [];
  let cursor = 0;
  while (cursor < value.length) {
    const start = value.indexOf("<", cursor);
    if (start < 0) break;
    let position = start + 1;
    let closing = false;
    if (value[position] === "/") {
      closing = true;
      position += 1;
    }
    while (position < value.length && /\s/.test(value[position])) position += 1;
    const nameStart = position;
    while (
      position < value.length &&
      isTagNameCharacter(value[position])
    ) {
      position += 1;
    }
    if (position === nameStart) {
      cursor = start + 1;
      continue;
    }
    const name = value.slice(nameStart, position).toLowerCase();
    const attributes: Record<string, string> = {};
    let end = -1;
    while (position < value.length) {
      while (position < value.length && /\s/.test(value[position])) position += 1;
      if (value[position] === ">") {
        end = position + 1;
        break;
      }
      if (value[position] === "/" && value[position + 1] === ">") {
        end = position + 2;
        break;
      }
      const attributeStart = position;
      while (
        position < value.length &&
        isTagNameCharacter(value[position])
      ) {
        position += 1;
      }
      if (position === attributeStart) {
        position += 1;
        continue;
      }
      const attributeName = value
        .slice(attributeStart, position)
        .toLowerCase();
      while (position < value.length && /\s/.test(value[position])) position += 1;
      if (value[position] !== "=") {
        attributes[attributeName] = "";
        continue;
      }
      position += 1;
      while (position < value.length && /\s/.test(value[position])) position += 1;
      const quote = value[position] === "'" || value[position] === '"'
        ? value[position]
        : undefined;
      if (quote) position += 1;
      const valueStart = position;
      if (quote) {
        while (position < value.length && value[position] !== quote) position += 1;
      } else {
        while (
          position < value.length &&
          !/\s/.test(value[position]) &&
          value[position] !== ">"
        ) {
          position += 1;
        }
      }
      attributes[attributeName] = value.slice(valueStart, position);
      if (quote && value[position] === quote) position += 1;
    }
    if (end < 0) break;
    tags.push({ name, attributes, start, end, closing });
    cursor = end;
  }
  return tags;
}

function parseHighlightIds(value: string): string[] {
  const ids: string[] = [];
  let current = "";
  const flush = () => {
    const normalized = current
      .split("[").join("")
      .split("]").join("")
      .split('"').join("")
      .split("'").join("")
      .trim();
    if (normalized && !ids.includes(normalized)) ids.push(normalized);
    current = "";
  };
  for (const character of value) {
    if (character === "," || /\s/.test(character)) flush();
    else current += character;
  }
  flush();
  return ids;
}

export function extractCitationIds(value: unknown): string[] {
  if (typeof value !== "string" || !value) return [];
  const ids: string[] = [];
  const add = (candidate: unknown) => {
    const id = nonEmptyString(candidate);
    if (id && !ids.includes(id)) ids.push(id);
  };
  for (const tag of scanMarkupTags(value)) {
    if (tag.closing) continue;
    if (tag.name === "u") add(tag.attributes.id);
    add(tag.attributes["data-citation-id"]);
    if (tag.name === "iframe") {
      for (const id of parseHighlightIds(tag.attributes["data-highlight-ids"] ?? "")) {
        add(id);
      }
    }
  }
  return ids;
}

function decodeHtmlText(value: string): string {
  return value
    .split("&nbsp;").join(" ")
    .split("&amp;").join("&")
    .split("&lt;").join("<")
    .split("&gt;").join(">")
    .split("&quot;").join('"')
    .split("&#39;").join("'");
}

/**
 * Return the exact user-visible text from the narrow citation markup emitted
 * by research services. This intentionally does not interpret arbitrary HTML.
 */
export function visibleCitationText(value: string): string {
  let result = "";
  let cursor = 0;
  for (const tag of scanMarkupTags(value)) {
    result += value.slice(cursor, tag.start);
    cursor = tag.end;
  }
  result += value.slice(cursor);
  return decodeHtmlText(result).split(/\s+/).filter(Boolean).join(" ");
}

export function extractCitedValuesFromText(value: string): Record<string, string> {
  const values: Record<string, string> = {};
  const lower = value.toLowerCase();
  for (const tag of scanMarkupTags(value)) {
    if (tag.closing || tag.name !== "u") continue;
    const id = nonEmptyString(tag.attributes.id);
    if (!id || values[id]) continue;
    const closeStart = lower.indexOf("</u", tag.end);
    if (closeStart < 0) continue;
    const text = visibleCitationText(value.slice(tag.end, closeStart));
    if (text) values[id] = text;
  }
  return values;
}

/**
 * Preserve all known citation values while preferring user-facing text for
 * duplicate IDs. Citation display normalization is model-guided because
 * internal reference formats vary by source.
 */
export function mergeCitedValuesByUuid(
  accumulated: Readonly<Record<string, string>> | undefined,
  userFacing: Readonly<Record<string, string>> | undefined,
): Record<string, string> {
  return {
    ...(accumulated ?? {}),
    ...(userFacing ?? {}),
  };
}

function markdownFieldForCitation(
  markdown: string,
  citationId: string,
): string | undefined {
  const lines = markdown.split(/\r?\n/);
  const citedIndex = lines.findIndex(
    (line) => line.includes(citationId) && line.includes("|"),
  );
  if (citedIndex < 0) return undefined;

  let blockStart = citedIndex;
  while (blockStart > 0 && lines[blockStart - 1].includes("|")) {
    blockStart -= 1;
  }
  const cells = (line: string) =>
    line.trim().replace(/^\|/, "").replace(/\|$/, "").split("|")
      .map((cell) => cell.trim());
  const headers = cells(lines[blockStart]);
  const citedCells = cells(lines[citedIndex]);
  const citedColumn = citedCells.findIndex((cell) => cell.includes(citationId));
  return citedColumn >= 0 && citedColumn < headers.length
    ? nonEmptyString(headers[citedColumn])
    : undefined;
}

function recordFromPayload(
  citationId: string,
  options: {
    client: unknown;
    metadata?: Record<string, unknown>;
    fql?: Record<string, unknown>;
    fieldName?: string;
  },
): CitationNoteRecord {
  const metadata = options.metadata;
  const fql = options.fql;
  const record: CitationNoteRecord = {
    citation_id: citationId,
    source: sourceLabel(metadata, options.client),
    document: nonEmptyString(firstField(metadata, "documentName", "document_name")),
    category: nonEmptyString(firstField(metadata, "documentCategory", "document_category")),
    company: nonEmptyString(firstField(metadata, "companyName", "company_name")),
    published_date: nonEmptyString(firstField(metadata, "datePublished", "date_published")),
    ticker:
      nonEmptyString(firstField(fql, "ticker")) ??
      nonEmptyString(firstField(metadata, "ticker")),
    fql_formula: nonEmptyString(firstField(fql, "fqlFormula", "fql_formula")),
    field: nonEmptyString(options.fieldName),
  };
  for (const key of Object.keys(record) as Array<keyof CitationNoteRecord>) {
    if (!record[key]) delete record[key];
  }
  record.citation_id = citationId;
  return record;
}

/**
 * The one merge policy for the run-scoped citation index.
 *
 * Later records augment; they never delete. A sparse record arriving after a
 * richer one — a tabular row that knows only an FQL formula following a
 * research answer that knew the source and document — must not strip fields
 * that are already established, or a Note written later loses provenance a
 * Note written earlier had.
 */
export function mergeCitationNoteRecord(
  index: CitationNoteIndex,
  citationId: string,
  record: CitationNoteRecord,
): void {
  const existing = index[citationId] ?? { citation_id: citationId };
  for (const [key, value] of Object.entries(record)) {
    if (value) {
      (existing as unknown as Record<string, string>)[key] = value;
    }
  }
  index[citationId] = existing;
}

/**
 * Add all resolvable Markdown and Dictionary citations from one research tool
 * result to a mutable run-scoped index.
 */
export function ingestAgentResponse(
  index: CitationNoteIndex,
  agentResponse: unknown,
): void {
  const response = asRecord(agentResponse);
  const citations = response?.citations;
  if (!Array.isArray(citations)) return;

  for (const rawCitation of citations) {
    const citation = asRecord(rawCitation);
    if (!citation) continue;
    const client = citation.client;
    const topMetadata =
      asRecord(firstField(citation, "metadata", "citation_metadata")) ?? {};
    const fqlMap = asRecord(citation.FQL) ?? {};
    const citationData = firstField(citation, "citation_data", "citationData");

    if (typeof citationData === "string") {
      const ids = extractCitationIds(citationData);
      for (const key of Object.keys(fqlMap)) {
        if (key && !ids.includes(key)) ids.push(key);
      }
      for (const citationId of ids) {
        mergeCitationNoteRecord(index, citationId, recordFromPayload(citationId, {
          client,
          metadata: topMetadata,
          fql: asRecord(fqlMap[citationId]),
          fieldName: markdownFieldForCitation(citationData, citationId),
        }));
      }
      continue;
    }

    const dictionary = asRecord(citationData);
    if (!dictionary) continue;
    for (const [entryKey, rawEntry] of Object.entries(dictionary)) {
      const entry = asRecord(rawEntry);
      if (!entry) continue;
      const text = nonEmptyString(entry.text) ?? "";
      const ids = extractCitationIds(text);
      for (const id of extractCitationIds(entryKey)) {
        if (!ids.includes(id)) ids.push(id);
      }
      if (ids.length === 0) continue;
      const nestedMetadata = asRecord(entry.metadata) ?? {};
      const metadata = { ...topMetadata, ...nestedMetadata };
      for (const citationId of ids) {
        mergeCitationNoteRecord(index, citationId, recordFromPayload(citationId, {
          client,
          metadata,
          fql: asRecord(fqlMap[citationId]),
        }));
      }
    }
  }
}

/**
 * Extract display values from cited `<u>` spans for existing chat persistence.
 */
export function extractCitedValuesById(
  agentResponse: unknown,
): Record<string, string> {
  const citationValues: Record<string, string> = {};
  const response = asRecord(agentResponse);
  const citations = response?.citations;
  if (Array.isArray(citations)) {
    for (const rawCitation of citations) {
      const citation = asRecord(rawCitation);
      const citationData = citation &&
        firstField(citation, "citation_data", "citationData");
      if (typeof citationData === "string") {
        Object.assign(citationValues, extractCitedValuesFromText(citationData));
        continue;
      }
      const dictionary = asRecord(citationData);
      if (!dictionary) continue;
      for (const [entryKey, rawEntry] of Object.entries(dictionary)) {
        const entry = asRecord(rawEntry);
        if (entry) {
          Object.assign(
            citationValues,
            extractCitedValuesFromText(nonEmptyString(entry.text) ?? ""),
          );
        }
        Object.assign(citationValues, extractCitedValuesFromText(entryKey));
      }
    }
  }

  const userFacingValues: Record<string, string> = {};
  for (const field of ["reply", "answer", "content"]) {
    const text = nonEmptyString(response?.[field]);
    if (text) {
      Object.assign(userFacingValues, extractCitedValuesFromText(text));
    }
  }
  return mergeCitedValuesByUuid(citationValues, userFacingValues);
}

function displayValue(
  record: CitationNoteRecord,
  field: Exclude<keyof CitationNoteRecord, "citation_id">,
  fallback?: string,
): string | undefined {
  const value = nonEmptyString(record[field]) ?? fallback;
  if (!value) return undefined;
  const limit = NOTE_FIELD_LIMITS[field];
  return value.length <= limit
    ? value
    : `${value.slice(0, limit - 1).trimEnd()}…`;
}

function calculationForField(fieldName: string | undefined): string | undefined {
  const normalized = fieldName?.toLowerCase() ?? "";
  if (normalized.includes("forward") && normalized.includes("dividend yield")) {
    return "Consensus DPS estimate / closing share price × 100";
  }
  if (normalized.includes("dividend yield")) {
    return "Dividend per share / share price × 100";
  }
  return undefined;
}

export function formatSourceSection(record: CitationNoteRecord): string {
  const source = displayValue(record, "source", "External Source");
  const lines = [`Source: ${source}`];
  const fields: Array<[
    Exclude<keyof CitationNoteRecord, "citation_id">,
    string,
  ]> = [
    ["document", "Document"],
    ["category", "Type"],
    ["company", "Company"],
    ["published_date", "Published"],
  ];
  for (const [field, label] of fields) {
    const value = displayValue(record, field);
    if (value) lines.push(`${label}: ${value}`);
  }
  if ((record.source ?? "").toLowerCase().startsWith("factset")) {
    const ticker = displayValue(record, "ticker");
    const field = displayValue(record, "field");
    if (ticker) lines.push(`Identifier: ${ticker}`);
    if (field) lines.push(`Field: ${field}`);
    const calculation = calculationForField(field);
    if (calculation) lines.push(`Calculation: ${calculation}`);
    const formula = displayValue(record, "fql_formula");
    lines.push(
      formula
        ? `FactSet audit formula: ${formula}`
        : "FactSet audit formula: Not available for this derived or descriptive field",
    );
  }
  return lines.join("\n");
}

export function formatExcelNote(
  index: CitationNoteIndex,
  rawCitationIds: readonly string[],
): { noteText?: string; unresolvedIds: string[] } {
  const citationIds = Array.from(new Set(rawCitationIds));
  const unresolvedIds: string[] = [];
  const sections: string[] = [];
  const seenSections = new Set<string>();
  for (const citationId of citationIds) {
    const record = index[citationId];
    if (!record || typeof record !== "object") {
      unresolvedIds.push(citationId);
      continue;
    }
    const section = formatSourceSection(record);
    if (!seenSections.has(section)) {
      seenSections.add(section);
      sections.push(section);
    }
  }
  if (sections.length === 0) return { unresolvedIds };
  const noteText = sections.length === 1
    ? sections[0]
    : sections.map((section, index) => `Source ${index + 1}:\n${section}`).join("\n\n");
  if (noteText.length > MAX_EXCEL_NOTE_LENGTH) {
    return {
      unresolvedIds: Array.from(new Set([...unresolvedIds, ...citationIds])),
    };
  }
  return { noteText, unresolvedIds };
}

export function isGeneratedSourceNote(value: unknown): boolean {
  if (typeof value !== "string") return false;
  const normalized = value.trim();
  if (normalized.startsWith("Source:")) return true;
  if (!normalized.startsWith("Source ")) return false;
  const firstLineEnd = normalized.indexOf("\n");
  if (firstLineEnd < 0) return false;
  const firstLine = normalized.slice(0, firstLineEnd);
  if (!firstLine.endsWith(":")) return false;
  const numberText = firstLine.slice("Source ".length, -1);
  return Boolean(numberText) &&
    Array.from(numberText).every((character) => character >= "0" && character <= "9") &&
    normalized.slice(firstLineEnd + 1).startsWith("Source:");
}

function normalizeCitationIds(
  value: unknown,
  location: string,
): { ids?: string[]; error?: string } {
  if (!Array.isArray(value) || value.length === 0) {
    return { error: `${location} must be a non-empty array of exact citation IDs` };
  }
  const ids: string[] = [];
  for (let index = 0; index < value.length; index += 1) {
    const id = nonEmptyString(value[index]);
    if (!id) {
      return { error: `${location}[${index}] must be a non-empty string` };
    }
    if (!ids.includes(id)) ids.push(id);
  }
  return { ids };
}

function cellHasContent(cell: CellModification): boolean {
  return cell.formula === undefined &&
    Object.prototype.hasOwnProperty.call(cell, "value") &&
    cell.value !== null;
}

interface MatrixShape {
  rows: number;
  columns: number;
}

function matrixShape(
  value: unknown,
  location: string,
): { shape?: MatrixShape; error?: string } {
  if (!Array.isArray(value) || value.length === 0) {
    return { error: `${location} must be a non-empty rectangular matrix` };
  }
  if (!Array.isArray(value[0]) || value[0].length === 0) {
    return { error: `${location}[0] must be a non-empty row` };
  }
  const columns = value[0].length;
  for (let row = 0; row < value.length; row += 1) {
    if (!Array.isArray(value[row]) || value[row].length !== columns) {
      return {
        error: `${location}[${row}] must contain exactly ${columns} columns`,
      };
    }
  }
  return { shape: { rows: value.length, columns } };
}

function rangeWriteShape(
  range: RangeModification,
  location: string,
): { shape?: MatrixShape; error?: string } {
  const candidates = [
    ["values", range.values],
    ["formulas", range.formulas],
  ] as const;
  let selected: MatrixShape | undefined;
  for (const [field, matrix] of candidates) {
    if (matrix === undefined) continue;
    const result = matrixShape(matrix, `${location}.${field}`);
    if (result.error) return result;
    if (
      selected &&
      (selected.rows !== result.shape!.rows ||
        selected.columns !== result.shape!.columns)
    ) {
      return {
        error: `${location}.values and ${location}.formulas must have matching dimensions`,
      };
    }
    selected = result.shape;
  }
  return selected
    ? { shape: selected }
    : { error: `${location} must include values or formulas for cited cells` };
}

function rangeCoordinateHasContent(
  range: RangeModification,
  row: number,
  column: number,
): boolean {
  const value = range.values?.[row]?.[column];
  const formula = range.formulas?.[row]?.[column];
  return value !== undefined && value !== null &&
    (formula === undefined || formula === null);
}

function targetKey(sheetName: string, address: string): string {
  return `${sheetName.toLowerCase()}!${address.toUpperCase()}`;
}

function addTarget(
  targetMap: Map<string, CitationNoteTarget>,
  target: CitationNoteTarget,
): string | undefined {
  const key = targetKey(target.sheetName, target.address);
  const previous = targetMap.get(key);
  if (!previous) {
    targetMap.set(key, target);
    return undefined;
  }
  if (
    previous.noteText !== target.noteText ||
    previous.citationIds.join("\u0000") !== target.citationIds.join("\u0000")
  ) {
    return `Conflicting citation metadata targets ${target.sheetName}!${target.address}`;
  }
  return undefined;
}

function prepareCitedCell(
  cell: CellModification,
  sheetName: string,
  location: string,
  index: CitationNoteIndex,
  targetMap: Map<string, CitationNoteTarget>,
): { cell: CellModification; issues: string[] } {
  const record = cell as CellModification & { citationIds?: unknown };
  const { citationIds: rawCitationIds, ...cleanCell } = record;
  if (rawCitationIds === undefined) return { cell, issues: [] };
  if (!sheetName) {
    return {
      cell: cleanCell,
      issues: [`${location} requires sheetName for a cited write`],
    };
  }
  if (cell.action !== "set") {
    return {
      cell: cleanCell,
      issues: [`${location}.citationIds requires action "set"`],
    };
  }
  if (cell.address.includes("!")) {
    return {
      cell: cleanCell,
      issues: [
        `${location}.citationIds requires a worksheet-local A1 address; ` +
        "use the enclosing sheetName instead of a sheet-qualified address",
      ],
    };
  }
  const parsedAddress = parseSingleCellAddress(cell.address);
  if (!parsedAddress) {
    return {
      cell: cleanCell,
      issues: [`${location}.citationIds requires one valid A1 cell address`],
    };
  }
  if (!cellHasContent(cell)) {
    return {
      cell: cleanCell,
      issues: [
        `${location}.citationIds requires a non-empty hardcoded value; ` +
        "formula cells are not citation targets",
      ],
    };
  }
  const normalized = normalizeCitationIds(rawCitationIds, `${location}.citationIds`);
  if (normalized.error) return { cell: cleanCell, issues: [normalized.error] };
  const formatted = formatExcelNote(index, normalized.ids!);
  if (formatted.unresolvedIds.length > 0 || !formatted.noteText) {
    return {
      cell: cleanCell,
      issues: [
        `${location}.citationIds contains unresolved citation metadata: ` +
        formatted.unresolvedIds.join(", "),
      ],
    };
  }
  const target: CitationNoteTarget = {
    sheetName,
    address: parsedAddress.normalizedAddress,
    citationIds: normalized.ids!,
    noteText: formatted.noteText,
  };
  const targetIssue = addTarget(targetMap, target);
  return {
    cell: cleanCell,
    issues: targetIssue ? [targetIssue] : [],
  };
}

function prepareCitedRange(
  range: RangeModification,
  sheetName: string,
  location: string,
  index: CitationNoteIndex,
  targetMap: Map<string, CitationNoteTarget>,
): { range: RangeModification; issues: string[] } {
  const record = range as RangeModification & { citationIdsMatrix?: unknown };
  const { citationIdsMatrix: rawMatrix, ...cleanRange } = record;
  if (rawMatrix === undefined) return { range, issues: [] };
  if (!sheetName) {
    return {
      range: cleanRange,
      issues: [`${location} requires sheetName for a cited write`],
    };
  }
  if (range.action !== "fill") {
    return {
      range: cleanRange,
      issues: [`${location}.citationIdsMatrix requires action "fill"`],
    };
  }
  if (range.address.includes("!")) {
    return {
      range: cleanRange,
      issues: [
        `${location}.citationIdsMatrix requires a worksheet-local A1 range; ` +
        "use the enclosing sheetName instead of a sheet-qualified address",
      ],
    };
  }
  const parsedRange = parseCellRangeAddress(range.address);
  if (!parsedRange) {
    return {
      range: cleanRange,
      issues: [
        `${location}.citationIdsMatrix requires a valid rectangular A1 range`,
      ],
    };
  }
  const writeShape = rangeWriteShape(range, location);
  if (writeShape.error) return { range: cleanRange, issues: [writeShape.error] };
  const citationShape = matrixShape(rawMatrix, `${location}.citationIdsMatrix`);
  if (citationShape.error) {
    return { range: cleanRange, issues: [citationShape.error] };
  }
  if (
    writeShape.shape!.rows !== citationShape.shape!.rows ||
    writeShape.shape!.columns !== citationShape.shape!.columns ||
    parsedRange.rowCount !== citationShape.shape!.rows ||
    parsedRange.columnCount !== citationShape.shape!.columns
  ) {
    return {
      range: cleanRange,
      issues: [
        `${location}.citationIdsMatrix dimensions must match both the write matrix ` +
        `and target range ${parsedRange.normalizedAddress}`,
      ],
    };
  }

  const issues: string[] = [];
  const matrix = rawMatrix as unknown[][];
  for (let row = 0; row < matrix.length; row += 1) {
    for (let column = 0; column < matrix[row].length; column += 1) {
      const rawIds = matrix[row][column];
      if (rawIds === null) continue;
      const cellAddress = formatCellAddress(
        parsedRange.startColumn + column,
        parsedRange.startRow + row,
      )!;
      if (!rangeCoordinateHasContent(range, row, column)) {
        issues.push(
            `${location}.citationIdsMatrix requires a non-empty hardcoded ` +
            `value at ${sheetName}!${cellAddress}`,
        );
        continue;
      }
      const normalized = normalizeCitationIds(
        rawIds,
        `${location}.citationIdsMatrix[${row}][${column}]`,
      );
      if (normalized.error) {
        issues.push(normalized.error);
        continue;
      }
      const formatted = formatExcelNote(index, normalized.ids!);
      if (formatted.unresolvedIds.length > 0 || !formatted.noteText) {
        issues.push(
            `${location}.citationIdsMatrix[${row}][${column}] contains unresolved ` +
            `citation metadata: ${formatted.unresolvedIds.join(", ")}`,
        );
        continue;
      }
      const target: CitationNoteTarget = {
        sheetName,
        address: cellAddress,
        citationIds: normalized.ids!,
        noteText: formatted.noteText,
      };
      const targetError = addTarget(targetMap, target);
      if (targetError) issues.push(targetError);
    }
  }
  return { range: cleanRange, issues };
}

/**
 * Strip citation-only fields and append generated Note annotations to the same
 * workbook modification. Malformed or unresolved citation metadata is retained
 * only as an internal issue and never blocks the underlying workbook write.
 */
export function prepareCitationWrites(
  modifications: WorkbookModification,
  index: CitationNoteIndex,
): PreparedCitationWrites {
  const targetMap = new Map<string, CitationNoteTarget>();
  const worksheets: WorksheetModification[] = [];
  const issues: string[] = [];

  for (let worksheetIndex = 0; worksheetIndex < (modifications.worksheets ?? []).length; worksheetIndex += 1) {
    const worksheet = modifications.worksheets![worksheetIndex];
    const sheetName = worksheet.sheetName?.trim() ?? "";

    const cells: CellModification[] = [];
    for (let cellIndex = 0; cellIndex < (worksheet.cells ?? []).length; cellIndex += 1) {
      const prepared = prepareCitedCell(
        worksheet.cells![cellIndex],
        sheetName,
        `modifications.worksheets[${worksheetIndex}].cells[${cellIndex}]`,
        index,
        targetMap,
      );
      issues.push(...prepared.issues);
      cells.push(prepared.cell);
    }

    const ranges: RangeModification[] = [];
    for (let rangeIndex = 0; rangeIndex < (worksheet.ranges ?? []).length; rangeIndex += 1) {
      const prepared = prepareCitedRange(
        worksheet.ranges![rangeIndex],
        sheetName,
        `modifications.worksheets[${worksheetIndex}].ranges[${rangeIndex}]`,
        index,
        targetMap,
      );
      issues.push(...prepared.issues);
      ranges.push(prepared.range);
    }

    worksheets.push({
      ...worksheet,
      ...(worksheet.cells ? { cells } : {}),
      ...(worksheet.ranges ? { ranges } : {}),
    });
  }

  const preparedModifications: WorkbookModification = {
    ...modifications,
    ...(modifications.worksheets ? { worksheets } : {}),
  };
  for (const annotation of preparedModifications.annotations ?? []) {
    if (annotation.action !== "set") continue;
    const key = targetKey(annotation.sheetName, annotation.address);
    if (targetMap.has(key)) {
      targetMap.delete(key);
      issues.push(
        `Citation Note target ${annotation.sheetName}!${annotation.address} ` +
          "conflicts with an explicit annotation in the same write",
      );
    }
  }

  const targets = Array.from(targetMap.values());
  if (targets.length === 0) {
    return { modifications: preparedModifications, targets, issues };
  }

  const generatedAnnotations: CellAnnotationOperation[] = targets.map((target) => ({
    kind: "note",
    action: "set",
    sheetName: target.sheetName,
    address: target.address,
    text: target.noteText,
    replaceExisting: false,
    origin: "citation",
  }));

  return {
    modifications: {
      ...preparedModifications,
      annotations: [
        ...(preparedModifications.annotations ?? []),
        ...generatedAnnotations,
      ],
    },
    targets,
    issues,
  };
}

export function citationCellKey(
  sheetName: string,
  address: string,
): string {
  const parsed = parseSingleCellAddress(address);
  return `${sheetName}!${parsed?.normalizedAddress ?? address.trim().toUpperCase()}`;
}

export function buildSourceNoteAudit(
  targets: readonly CitationNoteTarget[],
  actualNotes: ReadonlyMap<string, string | undefined>,
): SourceNoteAudit {
  const expectedCells: string[] = [];
  const presentCells: string[] = [];
  const missingCells: string[] = [];
  const unresolvedCells: string[] = [];
  for (const target of targets) {
    const cell = citationCellKey(target.sheetName, target.address);
    expectedCells.push(cell);
    const actual = actualNotes.get(cell);
    if (
      actual !== undefined &&
      normalizeNoteText(actual) === normalizeNoteText(target.noteText)
    ) {
      presentCells.push(cell);
    } else if (actual === "") missingCells.push(cell);
    else unresolvedCells.push(cell);
  }
  return {
    expectedCells,
    presentCells,
    missingCells,
    unresolvedCells,
    clean: missingCells.length === 0 && unresolvedCells.length === 0,
  };
}

function normalizeNoteText(value: string): string {
  return value
    .split("\r\n").join("\n")
    .split("\r").join("\n")
    .trim();
}

/**
 * Collapse successive audits to the latest observed state of each cell. This
 * lets a later corrected cited write replace an earlier missing/mismatched
 * result instead of leaving the run permanently dirty.
 */
export function mergeSourceNoteAudits(
  audits: readonly SourceNoteAudit[],
  activeCells?: readonly string[],
): SourceNoteAudit | undefined {
  if (audits.length === 0) return undefined;
  const order: string[] = [];
  const displayByKey = new Map<string, string>();
  const status = new Map<string, "present" | "missing" | "unresolved">();
  const addCell = (cell: string) => {
    const key = cell.toLowerCase();
    if (!displayByKey.has(key)) {
      displayByKey.set(key, cell);
      order.push(key);
    }
    return key;
  };
  for (const audit of audits) {
    for (const cell of audit.expectedCells) {
      addCell(cell);
    }
    for (const cell of audit.presentCells) status.set(addCell(cell), "present");
    for (const cell of audit.missingCells) status.set(addCell(cell), "missing");
    for (const cell of audit.unresolvedCells) status.set(addCell(cell), "unresolved");
  }

  const activeKeys = activeCells === undefined
    ? order
    : Array.from(new Set(activeCells.map((cell) => cell.toLowerCase())));
  if (activeKeys.length === 0) return undefined;
  for (const cell of activeCells ?? []) {
    displayByKey.set(cell.toLowerCase(), cell);
  }
  const expectedCells = activeKeys.map(
    (key) => displayByKey.get(key) ?? key,
  );
  const presentCells = activeKeys
    .filter((key) => status.get(key) === "present")
    .map((key) => displayByKey.get(key) ?? key);
  const missingCells = activeKeys
    .filter((key) => status.get(key) === "missing")
    .map((key) => displayByKey.get(key) ?? key);
  const unresolvedCells = activeKeys
    .filter((key) => status.get(key) !== "present" && status.get(key) !== "missing")
    .map((key) => displayByKey.get(key) ?? key);
  return {
    expectedCells,
    presentCells,
    missingCells,
    unresolvedCells,
    clean: missingCells.length === 0 && unresolvedCells.length === 0,
  };
}
